import {
  FC,
  ReactNode,
  useState,
  Key,
  useRef,
  useEffect,
  useMemo,
  memo,
} from 'react';
import SuperTable, { SuperTableProps } from '../SuperTable';
import useMainColumns from './hooks/useMainColumns';
import { ExpandableConfig } from 'antd/lib/table/interface';
import { TableActionType } from '../SuperTable/interface';
import { includeArray, } from '../utils/dataUtils';
import { formatListData, getParentKeys, getGroupTotal, formatGroupList } from './utils';
import './style.less';

export type selectTargetType = 'main' | 'sub' | 'all';
export type rowRenderType = (record: any) => ReactNode;
/** 主子表 */
export interface MainSubTableProps extends SuperTableProps {
  /** 可选时可选中对象 */
  selectTarget?: selectTargetType;
  /** 自动分组字段 */
  groupKeys?: string | string[];
  /** 子表对应字段 */
  subDataKey?: string;
  /** 子表key对应字段 */
  subRowKey?: string | ((record: any) => string);
  /** 默认展示子表长度 */
  defaultChildrenLength?: number | false;
  /** 分组小计字段 */
  groupTotal?: string[];
  /** 主表自定义行 */
  rowRender?: rowRenderType;
  /** 初始时，是否展开所有行 */
  defaultExpandAllRows?: boolean;
  /** 关闭内部控制rowSelection */
  insideRowSelection?: boolean;
  /** 是否显示多选框计数 */
  showSelectedCount?: boolean | ((selectedRowKeys: Key[], selectedRows: any[]) => number);
  /** 选中项发生改变时调用 */
  onSelectChange?: (selectedRowKeys: Key[], selectedRows: any[]) => void;
  /** 多选禁用判断 */
  disabledRow?: (record: any) => boolean;
  /** 自定义隐藏剩余条数文字 */
  onZoomText?: (count: number, record: any) => ReactNode;
}
const baseExpandable: ExpandableConfig<any> = {
  defaultExpandAllRows: true,
};
/**
 * 注意
 * 主子表列的配置，第一列不能用renderType属性
 */
const MainSubTable: FC<MainSubTableProps> = memo(props => {
  const {
    expandable,
    columns,
    groupKeys,
    rowRender,
    selectTarget = 'all',
    subDataKey = 'children',
    rowKey,
    subRowKey,
    defaultExpandAllRows,
    defaultChildrenLength = 3,
    insideRowSelection,
    groupTotal,
    rowSelection,
    pagination,
    headerTip,
    showSelectedCount,
    postData,
    onSelectChange,
    disabledRow,
    actionRef,
    onFetchComplete,
    onZoomText,
    dataSource: dataSourceProps,
    ...resProps
  } = props;
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [defaultExpandedRowKeys, setDefaultExpandedRowKeys] = useState<
    string[]
  >([]);
  const [selectedInfo, setSelectedInfo] = useState<Map<Key, any>>(new Map<Key, any>());
  const allDataSource = useRef<any[]>(); // 主子表数据
  const tableRef = useRef<TableActionType>();
  useEffect(() => {
    const newDataSoure = dataSourceProps || [];
    setDataSource(newDataSoure);
    if (defaultExpandAllRows) {
      const expandedRowKeys: string[] = getParentKeys(newDataSoure);
      setDefaultExpandedRowKeys(expandedRowKeys);
    }
  }, [dataSourceProps]);
  const currentPagination = useMemo(() => {
    if (pagination === false) return false;
    return {
      pageSizeOptions: ['5', '10', '20', '50'],
      pageSize: 10,
      ...pagination,
    };
  }, [pagination]);
  const handleZoom = (parnetKey: string, status: boolean) => {
    if (!defaultChildrenLength) return;
    const findItem = allDataSource.current?.find(v => v.key === parnetKey);
    const tableData = tableRef?.current?.getDataSource();
    if (!tableData) return;
    const newDataSource = tableData.map(item => {
      if (item.key === parnetKey) {
        let findItemChildren = findItem.children;
        if (Array.isArray(findItemChildren)) {
          const surplusNum = findItemChildren.length - defaultChildrenLength;
          const childrenData = status
            ? findItemChildren.slice(0, defaultChildrenLength)
            : [...findItemChildren];
          childrenData.push({
            surplusNum,
            isChildrenHidden: status,
            key: `${item.key}-${findItemChildren.length}`,
            parnetKey: item.key,
          });
          if (groupTotal) {
            const totalData = getGroupTotal(groupTotal, findItemChildren);
            childrenData.push({
              isTotal: true,
              ...totalData,
            });
          }
          return {
            ...findItem,
            children: childrenData,
          };
        }
        return item;
      }
      return item;
    });
    setDataSource(newDataSource);
  };
  const newColumns = useMainColumns(
    columns,
    groupTotal,
    rowRender,
    handleZoom,
    onZoomText,
  );
  useEffect(() => {
    onSelectChange?.([...selectedInfo.keys()], [...selectedInfo.values()]);
  }, [selectedInfo]);
  const handlePostData = (data: any[], params: any) => {
    let newDataSoure = groupKeys ? formatGroupList(data, groupKeys, params, rowKey) : formatListData(
      data,
      params,
      subDataKey,
      rowKey,
      subRowKey,
    );
    if (defaultExpandAllRows) {
      const expandedRowKeys: string[] = getParentKeys(newDataSoure);
      setDefaultExpandedRowKeys(expandedRowKeys);
    }
    if (postData) {
      newDataSoure = postData?.(newDataSoure, params);
    }
    if (!defaultChildrenLength) return newDataSoure;
    allDataSource.current = newDataSoure;
    const simpleDataSoure = newDataSoure.map(v => {
      const childrenData = v.children;
      if (
        Array.isArray(childrenData) &&
        childrenData.length > defaultChildrenLength
      ) {
        const sliceChildrenData = childrenData.slice(0, defaultChildrenLength);
        sliceChildrenData.push({
          isChildrenHidden: true,
          surplusNum: childrenData.length - defaultChildrenLength,
          parnetKey: v.key,
          key: `${v.key}-${childrenData.length}`,
        });
        if (groupTotal) {
          const totalData = getGroupTotal(groupTotal, childrenData);
          sliceChildrenData.push({
            isTotal: true,
            ...totalData,
          });
        }
        return {
          ...v,
          isAllChildren: false,
          children: sliceChildrenData,
        };
      }
      if (groupTotal && Array.isArray(childrenData) && childrenData.length) {
        const totalData = getGroupTotal(groupTotal, childrenData);
        return {
          ...v,
          children: [
            ...childrenData,
            {
              isTotal: true,
              ...totalData,
            },
          ],
        };
      }
      return v;
    });
    return simpleDataSoure;
  };
  const handleExpand = (expanded: boolean, record: any) => {
    if (expanded) {
      setDefaultExpandedRowKeys([...defaultExpandedRowKeys, record.key]);
    } else {
      setDefaultExpandedRowKeys(
        defaultExpandedRowKeys.filter(v => v !== record.key),
      );
    }
  };
  const handleSelect = (record: any, selected: boolean) => {
    const newSelectedInfo: Map<Key, any> = new Map([...selectedInfo]);
    if (selected) {
      if (!newSelectedInfo.has(record.key)) newSelectedInfo.set(record.key, record);
      if (selectTarget === 'all') {
        let childrenData = record.children;
        const parentKey = record.parentKey;
        if (Array.isArray(childrenData)) {
          childrenData = childrenData.filter(
            v => v.isChildrenHidden === undefined && !v.isTotal,
          );
          childrenData.forEach((item: any) => {
            if (!newSelectedInfo.has(item.key)) newSelectedInfo.set(item.key, item);
          });
        }
        if (parentKey) {
          const tableData = tableRef?.current?.getDataSource();
          const findItem = tableData?.find(v => v.key === parentKey);
          const childrenKeys: any[] = findItem?.children
            ?.filter?.(
              (v: any) => v.isChildrenHidden === undefined && !v.isTotal,
            )
            .map((v: any) => v.key);
          if (includeArray([...newSelectedInfo.keys()], childrenKeys)) {
            if (!newSelectedInfo.has(parentKey)) newSelectedInfo.set(parentKey, findItem);
          }
        }
      }
    } else {
      newSelectedInfo.delete(record.key);
      if (selectTarget === 'all') {
        if (record.parentKey) {
          newSelectedInfo.delete(record.parentKey);
        }
        const childrenData = record.children;
        if (Array.isArray(childrenData)) {
          childrenData.forEach(item => {
            newSelectedInfo.delete(item.key);
          });
        }
      }
    }
    setSelectedInfo(newSelectedInfo);
  };
  const getCheckboxDisabled = (record: any): boolean => {
    if (selectTarget === 'main' && !record.isMainTable) return true;
    if (selectTarget === 'sub' && record.isMainTable) return true;
    if (record.isChildrenHidden !== undefined) return true;
    if (record.isTotal) return true;
    if (disabledRow) return disabledRow(record);
    return false;
  };
  const handleRowClassName = (record: any): string => {
    if (record.isTotal) return 'main-hide-checkbox main-table-total-row';
    if (record.isMainTable && selectTarget === 'sub')
      return 'main-hide-checkbox';
    if (record.isChildrenHidden !== undefined) return 'more-hide-checkbox';
    if (
      !record.isMainTable &&
      record.isChildrenHidden === undefined &&
      selectTarget === 'main'
    )
      return 'sub-hide-checkbox';
    return '';
  };
  if (insideRowSelection && rowSelection && rowSelection.type === 'checkbox') {
    rowSelection.selectedRowKeys = [...selectedInfo.keys()];
    rowSelection.getCheckboxProps = (record: any) => ({
      disabled: getCheckboxDisabled(record),
    });
    rowSelection.onSelect = (record: any, selected: boolean) => {
      handleSelect(record, selected);
    };
    rowSelection.onSelectAll = (selected, selectedRows, changeRows) => {
      let effectiveRow: any[] = [];
      const newSelectedInfo: Map<Key, any> = new Map([...selectedInfo]);
      if (selected) {
        if (selectTarget === 'all') effectiveRow = changeRows;
        if (selectTarget === 'main') {
          effectiveRow = changeRows.filter(v => v.isMainTable);
        }
        if (selectTarget === 'sub') {
          effectiveRow = changeRows.filter(
            v => !v.isMainTable && v.isChildrenHidden === undefined,
          );
        }
        effectiveRow.forEach(v => {
          if (!newSelectedInfo.has(v.key)) {
            newSelectedInfo.set(v.key, v);
          }
        });
      } else {
        changeRows.forEach(item => {
          newSelectedInfo.delete(item.key);
        });
      }
      setSelectedInfo(newSelectedInfo);
    };
  }
  /** 列表头部提示 */
  const headerTipNode = useMemo(() => {
    if (showSelectedCount) {
      let selectedLength: number = typeof showSelectedCount === 'function' ? showSelectedCount([...selectedInfo.keys()], [...selectedInfo.values()]) : selectedInfo.size;
      if (selectedLength) {
        return (
          <div className='select-row-config'>
              <span>已选择</span>
              <span className='select-row-config-num'>{selectedLength}</span>
              <span>项</span>
              <span
                className='select-row-config-empty'
                onClick={() => {
                  setSelectedInfo(new Map<Key, any>());
                  tableRef?.current?.emptySelect();
                }}
              >清空</span>
            </div>
        );
      }
    }
  }, [showSelectedCount, selectedInfo, selectTarget]);

  if (actionRef) {
    actionRef.current = tableRef.current;
    if (actionRef.current) {
      actionRef.current.emptySelect = () => {
        setSelectedInfo(new Map<Key, any>());
        tableRef?.current?.emptySelect();
      }
    }
  }

  return (
    <div className="yb-main-table">
      <SuperTable
        dataSource={dataSource}
        actionRef={tableRef}
        columns={newColumns}
        operation={false}
        headOperationAfter={headerTipNode}
        postData={handlePostData}
        rowKey="key"
        chilrenKey={subDataKey}
        rowClassName={handleRowClassName}
        onFetchComplete={(data: any[], paginationParams: any) => {
          onFetchComplete?.(data, paginationParams);
        }}
        defaultUnrepeated={false}
        pagination={currentPagination}
        isMainSubTable={true}
        expandable={{
          ...baseExpandable,
          onExpand: handleExpand,
          expandedRowKeys: defaultExpandedRowKeys,
          ...expandable,
        }}
        rowSelection={rowSelection}
        {...resProps}
      />
    </div>
  );
});

MainSubTable.defaultProps = {
  subDataKey: 'children',
  defaultExpandAllRows: true,
  defaultChildrenLength: 3,
  selectTarget: 'all',
  insideRowSelection: true,
};
export default MainSubTable;
